import { API_BASE_URL } from './api';

// Types for the clinics API
export interface ClinicInfo {
  agency_id: string;
  clinic_id: string;
  clinic_name: string;
  auto_mrn: boolean;
  physicians: Array<{
    name: string;
    id: string;
  }>;
  clinicians: Array<{
    name: string;
    id: string;
  }>;
  clinical_care_managers: Array<{
    name: string;
    id: string;
  }>;
  case_managers: Array<{
    name: string;
    id: string;
  }>;
  oasis_approvers: Array<{
    name: string;
    id: string;
  }>;
  pt_approvers: Array<{
    name: string;
    id: string;
  }>;
  ot_approvers: Array<{
    name: string;
    id: string;
  }>;
  st_approvers: Array<{
    name: string;
    id: string;
  }>;
  primary_schedulers: Array<{
    name: string;
    id: string;
  }>;
  secondary_schedulers: Array<{
    name: string;
    id: string;
  }>;
  insurance_providers?: Array<{
    name: string;
    id: string;
    type: number;
    payer_id: string;
    clearinghouse: string;
  }>;
  branches: Array<{
    name: string;
    id: string;
  }>;
}

export type ClinicInfoResponse = ClinicInfo[];

export interface AgencyInsuranceResponse {
  id: string;
  name: string;
  agency_id: string;
  payor_id?: string;
  edi_payer_id: string;
  edi_clearinghouse: string;
  clinic_key: string;
  clinic_name: string;
  insurance_key: string;
  visit_authorization_required?: boolean;
  display_in_auth_manager?: boolean;
  stedi_eligibility_enabled?: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface AgencyClinicResponse {
  id: string;
  agency_id: string;
  name: string;
  clinic_key: string;
  npi?: string;
  ein?: string;
  physicians?: Record<string, unknown>[];
  clinicians?: Record<string, unknown>[];
  clinical_care_managers?: Record<string, unknown>[];
  case_managers?: Record<string, unknown>[];
  oasis_approvers?: Record<string, unknown>[];
  pt_approvers?: Record<string, unknown>[];
  ot_approvers?: Record<string, unknown>[];
  st_approvers?: Record<string, unknown>[];
  primary_schedulers?: Record<string, unknown>[];
  secondary_schedulers?: Record<string, unknown>[];
  branches?: Record<string, unknown>[];
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

export interface AgencyClinicCreate {
  agency_id: string;
  name: string;
  clinic_key: string;
  npi?: string;
  ein?: string;
  physicians?: Record<string, unknown>[];
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
}

export interface AgencyClinicUpdate {
  name?: string;
  npi?: string;
  ein?: string;
  physicians?: Record<string, unknown>[];
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
}

/**
 * Get clinic information from RogerDoc API for a specific agency
 */
export const getClinicInfo = async (
  agencyId: string
): Promise<ClinicInfoResponse> => {
  // Build URL path with agency_id and optional clinic_id
  const url = `${API_BASE_URL}/rogerdoc/api/clinics/info/${encodeURIComponent(agencyId)}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch clinic info: ${response.statusText}`);
  }

  return response.json();
};
/**
 * Get agency insurance records
 */
export const getAgencyInsurances = async (
  agencyId?: string,
  clinicKey?: string,
  skip?: number,
  limit?: number
): Promise<AgencyInsuranceResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  if (clinicKey) {
    params.append('clinic_key', clinicKey);
  }
  if (skip !== undefined) {
    params.append('skip', skip.toString());
  }
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }

  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch agency insurances: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Update agency insurance records from clinic information
 */
export const updateInsurancesFromClinicInfo = async (
  clinicInfo: ClinicInfoResponse
): Promise<{
  deleted_count: number;
  created_count: number;
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/clinics/insurance/update-from-clinic-info`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clinicInfo),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update insurances: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get agency clinics
 */
export const getAgencyClinics = async (
  agencyId?: string,
  skip: number = 0,
  limit: number = 100
): Promise<AgencyClinicResponse[]> => {
  const params = new URLSearchParams();
  if (agencyId) {
    params.append('agency_id', agencyId);
  }
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());

  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinics: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a specific agency clinic by ID
 */
export const getAgencyClinic = async (
  clinicId: string
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new agency clinic
 */
export const createAgencyClinic = async (
  clinicData: AgencyClinicCreate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an agency clinic
 */
export const updateAgencyClinic = async (
  clinicId: string,
  clinicData: AgencyClinicUpdate
): Promise<AgencyClinicResponse> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(clinicData),
  });

  if (!response.ok) {
    throw new Error(`Failed to update agency clinic: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete an agency clinic
 */
export const deleteAgencyClinic = async (clinicId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/agency-clinics/${clinicId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete agency clinic: ${response.statusText}`);
  }
};

/**
 * Sync clinics and insurances from clinic information by fetching from RogerDoc API
 */
export const syncClinicsFromClinicInfo = async (): Promise<{
  clinics: {
    created_count: number;
    updated_count: number;
    skipped_count: number;
    total_count: number;
  };
  insurances: {
    created_count: number;
    updated_count: number;
    skipped_count: number;
    total_count: number;
  };
  message: string;
}> => {
  const response = await fetch(
    `${API_BASE_URL}/agency-clinics/sync-from-clinic-info`,
    {
      method: 'POST',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to sync clinics: ${response.statusText}`);
  }

  return response.json();
};
